import { workboardPrefixExists } from 'dashboard409a/services/dashboard';
import { DEFAULT_DROPBOX_ROOT, DROPBOX_ROOT_OPTIONS, ISRAEL_DROPBOX_ROOT } from './dropbox.constants';
import {
  buildDropboxPath,
  getDropboxFolderDisplayName,
  normalizeFolderSegment,
  resolveDropboxPath,
  stripDropboxRootPrefix,
} from './dropbox.utils';

export const dropboxRootToStorageRoot = (dropboxRoot: string): string => dropboxRoot.replace(/^\//, '');

const STORAGE_ROOT_PREFIXES = DROPBOX_ROOT_OPTIONS.map(root => `${dropboxRootToStorageRoot(root)}/`);

/**
 * Which valuation root a stored reference or absolute path belongs to. Saved
 * references carry their own root, so readers should not assume the default.
 */
export const dropboxRootFromReference = (reference: string | null | undefined): string => {
  const absolute = buildDropboxPath(reference || '');
  const israelRoot = buildDropboxPath(ISRAEL_DROPBOX_ROOT);
  return absolute === israelRoot || absolute.startsWith(`${israelRoot}/`)
    ? ISRAEL_DROPBOX_ROOT
    : DEFAULT_DROPBOX_ROOT;
};

/**
 * Normalize a stored folder reference to a Dropbox-backup S3 object key prefix
 * (no leading slash; Valuation/ or Valuation - Israel/ root when applicable).
 */
export const normalizeFileStorageReferenceForS3 = (reference: string): string => {
  const key = (reference || '').trim().replace(/^\/+/, '');
  if (!key) {
    return key;
  }
  if (STORAGE_ROOT_PREFIXES.some(prefix => key.startsWith(prefix))) {
    return key;
  }
  const relative = stripDropboxRootPrefix(buildDropboxPath(reference), DROPBOX_ROOT_OPTIONS);
  const root = dropboxRootToStorageRoot(dropboxRootFromReference(reference));
  return `${root}/${relative || key}`;
};

/** Stored company/CMD reference -> workboard API prefix, e.g. `Acme Corp/`. */
export const toWorkboardRelativePrefixFromStorageReference = (
  reference: string | null | undefined,
  dropboxRoot: string = DEFAULT_DROPBOX_ROOT
): string => {
  if (!reference?.trim()) {
    return '';
  }

  let storageKey = reference.trim();
  if (storageKey.startsWith('/')) {
    storageKey = normalizeFileStorageReferenceForS3(buildDropboxPath(storageKey).slice(1));
  } else if (!storageKey.startsWith('Valuation')) {
    storageKey = normalizeFileStorageReferenceForS3(
      `${dropboxRootToStorageRoot(dropboxRoot)}/${normalizeFolderSegment(storageKey)}`
    );
  } else {
    storageKey = normalizeFileStorageReferenceForS3(storageKey);
  }

  const relative = stripDropboxRootPrefix(storageKey, DROPBOX_ROOT_OPTIONS);
  if (!relative) {
    return '';
  }
  return relative.endsWith('/') ? relative : `${relative}/`;
};

export const workboardRelativePathToCompanyStorageReference = (
  relativePath: string,
  dropboxRoot: string
): string => {
  const segment = normalizeFolderSegment(relativePath);
  if (!segment) {
    return '';
  }
  return normalizeFileStorageReferenceForS3(`${dropboxRootToStorageRoot(dropboxRoot)}/${segment}`);
};

export const workboardRelativePathToValuationStorageReference = (
  relativePath: string,
  dropboxRoot: string
): string => {
  const segment = normalizeFolderSegment(relativePath);
  if (!segment) {
    return '';
  }
  return normalizeFileStorageReferenceForS3(`${dropboxRootToStorageRoot(dropboxRoot)}/${segment}`);
};

export const valuationFolderNameFromWorkboardPath = (
  relativePath: string,
  companyRelativePrefix: string
): string => {
  const normalizedPath = normalizeFolderSegment(relativePath);
  const companyPrefix = normalizeFolderSegment(companyRelativePrefix);
  if (companyPrefix && normalizedPath.startsWith(`${companyPrefix}/`)) {
    // Keep the full nested remainder under the company folder (not only the first segment).
    return normalizedPath.slice(companyPrefix.length + 1) || getDropboxFolderDisplayName(normalizedPath);
  }
  return getDropboxFolderDisplayName(normalizedPath);
};

/** Valuation-root-relative prefix for workboard S3 directory APIs, e.g. `Acme/2024-01-01/`. */
export const toWorkboardRelativePrefix = (valuationFolderPath: string): string => {
  const absolute = valuationFolderPath.startsWith('/')
    ? valuationFolderPath
    : buildDropboxPath(valuationFolderPath);
  const relative = stripDropboxRootPrefix(absolute, DROPBOX_ROOT_OPTIONS);
  if (!relative) {
    return '';
  }
  return relative.endsWith('/') ? relative : `${relative}/`;
};

/** Resolve the absolute valuation folder path from company + CMD references. */
export const resolveValuationFolderPath = (
  companyRef: string | null | undefined,
  cmdRef: string | null | undefined,
  dropboxRoot: string
): string | null => {
  const folderRef = cmdRef?.trim();
  if (!folderRef) {
    return null;
  }
  if (folderRef.startsWith('/')) {
    return resolveDropboxPath(folderRef, dropboxRoot);
  }

  const companyReference = companyRef?.trim() || '';
  if (!companyReference) {
    return null;
  }

  if (companyReference.startsWith('/') || companyReference.startsWith('Valuation')) {
    const companyPath = companyReference.startsWith('/')
      ? resolveDropboxPath(companyReference, dropboxRoot)
      : buildDropboxPath(companyReference);
    return buildDropboxPath(companyPath, folderRef);
  }

  const parentPath = resolveDropboxPath(companyReference, dropboxRoot);
  return parentPath ? buildDropboxPath(parentPath, folderRef) : null;
};

/** Normalize a CMD-relative valuation folder segment (no Valuation/ prefix). */
export const normalizeCmdFileStorageReference = (reference: string): string =>
  normalizeFolderSegment(reference);

/**
 * Check that a folder exists in the Dropbox backup bucket.
 * Uses the single-request prefix check instead of enumerating backup directories.
 */
export const verifyBackupFolderExists = async (absolutePath: string): Promise<boolean> => {
  const normalized = normalizeFileStorageReferenceForS3(absolutePath);
  const relative = stripDropboxRootPrefix(normalized, DROPBOX_ROOT_OPTIONS);
  if (!relative) {
    return false;
  }

  try {
    // The path carries its own root, so scope the check to it rather than the default.
    return await workboardPrefixExists(
      relative.endsWith('/') ? relative : `${relative}/`,
      dropboxRootFromReference(normalized)
    );
  } catch {
    return false;
  }
};
